import React from 'react';
import DateInput from '../DateInput';
import { MAX_UPLOAD_MB } from '../../utils/starterValidation';

/**
 * The field groups both new starter forms share.
 *
 * The employer fills these in when they add a starter themselves; the new
 * starter fills the same ones in on the emailed form. Keeping one copy is the
 * point — a divergence here means two versions of what payroll considers a
 * complete record, and only one of them gets tested.
 *
 * Every group takes the same shape: `form`, `errors`, `onChange`, and an
 * `inputClass(field)` from the host page so focus rings and error borders match
 * whatever page the fields are rendered on.
 */

function FieldError({ children }) {
  if (!children) return null;
  return <p className="text-red-500 text-xs mt-1">{children}</p>;
}

const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

export function PersonalFields({ form, errors, onChange, inputClass, selfService = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={labelCls} htmlFor="starter-first-name">First Name *</label>
        <input id="starter-first-name" name="first_name" maxLength={100} value={form.first_name} onChange={onChange} className={inputClass('first_name')} />
        <FieldError>{errors.first_name}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-last-name">Last Name *</label>
        <input id="starter-last-name" name="last_name" maxLength={100} value={form.last_name} onChange={onChange} className={inputClass('last_name')} />
        <FieldError>{errors.last_name}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-email">Email *</label>
        <input id="starter-email" name="email" type="email" value={form.email} onChange={onChange}
          placeholder={selfService ? 'Where your payslips will go' : 'For payslip delivery'}
          className={inputClass('email')} />
        <FieldError>{errors.email}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-gender">
          Gender *
          <span className="relative inline-block ml-1 group">
            <svg className="w-3.5 h-3.5 inline text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-48 p-2 text-xs text-white bg-gray-800 rounded shadow-lg z-10 font-normal">
              Required by HMRC for payroll reporting purposes.
            </span>
          </span>
        </label>
        <select id="starter-gender" name="gender" value={form.gender} onChange={onChange} className={inputClass('gender')}>
          <option value="">-- Select --</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        <FieldError>{errors.gender}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-dob">Date of Birth *</label>
        <DateInput
          id="starter-dob"
          name="date_of_birth"
          value={form.date_of_birth}
          onChange={onChange}
          max={new Date().toISOString().split('T')[0]}
          openToDate={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 30); return d; })()}
          className={inputClass('date_of_birth')}
        />
        <FieldError>{errors.date_of_birth}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-ni">NI Number *</label>
        <input id="starter-ni" name="ni_number" value={form.ni_number} onChange={onChange} placeholder="AB123456C" className={inputClass('ni_number')} />
        {errors.ni_number
          ? <FieldError>{errors.ni_number}</FieldError>
          : selfService && <p className="text-xs text-gray-500 mt-1">On your payslip, P45 or letters from HMRC.</p>}
      </div>
    </div>
  );
}

export function AddressFields({ form, errors, onChange, inputClass }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className={labelCls} htmlFor="starter-address-1">Address Line 1 *</label>
        <input id="starter-address-1" name="address_line_1" value={form.address_line_1} onChange={onChange} className={inputClass('address_line_1')} />
        <FieldError>{errors.address_line_1}</FieldError>
      </div>
      <div className="md:col-span-2">
        <label className={labelCls} htmlFor="starter-address-2">Address Line 2</label>
        <input id="starter-address-2" name="address_line_2" value={form.address_line_2} onChange={onChange} className={inputClass('address_line_2')} />
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-city">City *</label>
        <input id="starter-city" name="city" value={form.city} onChange={onChange} className={inputClass('city')} />
        <FieldError>{errors.city}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-postcode">Postcode *</label>
        <input id="starter-postcode" name="postcode" value={form.postcode} onChange={onChange} className={inputClass('postcode')} />
        <FieldError>{errors.postcode}</FieldError>
      </div>
    </div>
  );
}

/**
 * Bank details. On the employee's own form `confirmAccountNumber` adds a second
 * account number field: the employer can't check these — they never had them —
 * so the only person who can catch a typo is the person typing it.
 */
export function BankFields({ form, errors, onChange, inputClass, confirmAccountNumber = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={labelCls} htmlFor="starter-account-name">Account Name *</label>
        <input id="starter-account-name" name="bank_account_name" value={form.bank_account_name} onChange={onChange} className={inputClass('bank_account_name')} />
        <FieldError>{errors.bank_account_name}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-sort-code">Sort Code *</label>
        <input id="starter-sort-code" name="sort_code" value={form.sort_code} onChange={onChange} placeholder="XX-XX-XX" className={inputClass('sort_code')} />
        <FieldError>{errors.sort_code}</FieldError>
      </div>
      <div>
        <label className={labelCls} htmlFor="starter-account-number">Account Number *</label>
        <input id="starter-account-number" name="account_number" value={form.account_number} onChange={onChange} placeholder="12345678" className={inputClass('account_number')} />
        <FieldError>{errors.account_number}</FieldError>
      </div>
      {confirmAccountNumber && (
        <div>
          <label className={labelCls} htmlFor="starter-account-number-confirm">Confirm Account Number *</label>
          <input id="starter-account-number-confirm" name="account_number_confirm" value={form.account_number_confirm || ''} onChange={onChange}
            placeholder="12345678" autoComplete="off" className={inputClass('account_number_confirm')} />
          {errors.account_number_confirm
            ? <FieldError>{errors.account_number_confirm}</FieldError>
            : <p className="text-xs text-gray-500 mt-1">Type it again — this is the only check on where your pay lands.</p>}
        </div>
      )}
    </div>
  );
}

/**
 * P45 or HMRC starter checklist. Required on both forms: it is the document the
 * payroll team needs to put the new starter on the right tax code.
 */
export function StarterDocumentFields({ form, errors, files, onToggleP45, onFileChange, selfService = false }) {
  const fileInputCls = 'mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100';
  const fileHint = <p className="text-xs text-gray-500 mt-1">PDF, JPG or PNG, up to {MAX_UPLOAD_MB}MB each. A clear photo is fine.</p>;
  return (
    <div className="space-y-4">
      <label className="flex items-center space-x-2">
        <input type="checkbox" checked={form.has_p45 === 'yes'}
          onChange={(e) => onToggleP45(e.target.checked ? 'yes' : 'no')}
          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {selfService ? 'I have a P45 from my last job' : 'Employee has provided P45'}
        </span>
      </label>

      {errors.documents && <p className="text-red-500 text-sm font-medium">{errors.documents}</p>}

      {form.has_p45 === 'yes' ? (
        <div>
          <label className={labelCls} htmlFor="starter-p45">
            {selfService ? 'Upload your P45 (all pages) *' : 'Upload P45 (multiple files supported) *'}
          </label>
          <input id="starter-p45" type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={onFileChange} className={fileInputCls} />
          {files.length > 1 && <p className="text-xs text-gray-500 mt-1">{files.length} files selected</p>}
          {fileHint}
        </div>
      ) : (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded space-y-2">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">New Starter Checklist Required</p>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Download the HMRC Starter Checklist:{' '}
            <a href="https://www.gov.uk/government/publications/paye-starter-checklist"
              target="_blank" rel="noopener noreferrer" className="underline font-medium">
              HMRC Starter Checklist
            </a>
          </p>
          <div className="mt-2">
            <label className={labelCls} htmlFor="starter-checklist">
              {selfService ? 'Upload your completed checklist *' : 'Upload completed checklist (multiple files supported) *'}
            </label>
            <input id="starter-checklist" type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={onFileChange} className={fileInputCls} />
            {files.length > 1 && <p className="text-xs text-gray-500 mt-1">{files.length} files selected</p>}
            {fileHint}
          </div>
        </div>
      )}
    </div>
  );
}
